import React from 'react';
import { useSelector } from 'react-redux';
import { Typography, Theme } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { useTranslation } from 'services/localization/useTranslation';
import Container from 'common/components/Container';
import Spinner from 'common/components/Spinner';
import ContainerBox from 'components/ContainerBox';
import * as ShopSelectors from 'selectors/ShopSelectors';
import AddCardDialog from 'components/PaymentMethods/AddCardDialog';
import SavedCards from './SavedCards';
import Invoices from './Invoices';
import { useForm } from 'react-hook-form';
import { Callable } from 'common/frontend/callable';
import { useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';

import BillingInformation, { BillingInformationFormState } from './BillingInformation';
import PlanInformation from './PlanInformation';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SaveButton from 'common/components/SaveButton';
import { Prompt } from 'react-router-dom';
import Banner from 'components/Banner';
import { getTotalCreditsBalance } from 'common/modules/billing/utils/credits';
import { getPricingString } from 'common/utils/common';

const DEFAULT_VAT_NUMBER = {
	value: '',
	validated: false,
	exempt: false,
};

const Billing = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const adyenPaymentMethods = useSelector(ShopSelectors.adyenPaymentMethods);
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopReadOnly = useSelector(ShopSelectors.activeShopReadOnly)!;
	const shopId = shopPublicInfo?.shopId;
	const classes = useStyles();

	const billingAddressForm = useForm<BillingInformationFormState>({
		defaultValues: {
			billingEmail: shopReadOnly?.billing?.billingEmail ?? {
				type: 'default',
			},
			billingAddress: shopReadOnly?.billing?.billingAddress ?? {
				type: 'default',
			},
			billingInformation: {
				businessName: shopReadOnly?.billing?.billingInformation?.businessName ?? '',
				businessId: shopReadOnly?.billing?.billingInformation?.businessId ?? '',
			},
			vatNumber: shopReadOnly?.billing?.vatNumber ?? DEFAULT_VAT_NUMBER,
		},
		mode: 'onChange',
	});

	const isDirty = billingAddressForm.formState.isDirty;
	const isValid = billingAddressForm.formState.isValid;
	const isSubmitting = billingAddressForm.formState.isSubmitting;
	const dirtyFields = billingAddressForm.formState.dirtyFields;

	const handleSubmitAll = () => {
		billingAddressForm.handleSubmit(handleBillingInformationSubmit)();
		// Add more forms here as needed
	};

	const handleBillingInformationSubmit = async (data: BillingInformationFormState) => {
		if (!shopId) return;

		try {
			const ops = [];
			if (!!dirtyFields.billingAddress) {
				ops.push(
					Callable.billing.updateBillingAddress({
						merchantId: shopId,
						address: data.billingAddress,
					}),
				);
			}
			if (!!dirtyFields.billingEmail) {
				ops.push(
					Callable.billing.updateBillingEmail({
						merchantId: shopId,
						email: data.billingEmail,
					}),
				);
			}

			if (!!dirtyFields.billingInformation) {
				ops.push(
					Callable.billing.updateBillingInformation({
						merchantId: shopId,
						billingInformation: data.billingInformation,
					}),
				);
			}

			if (!!dirtyFields.vatNumber) {
				ops.push(
					Callable.billing.updateVatNumber({
						merchantId: shopId,
						vatNumber: data.vatNumber.value,
						exempt: data.vatNumber.exempt ?? false,
					}),
				);
			}

			await Promise.all(ops);
			billingAddressForm.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const paymentMethodsLoading = adyenPaymentMethods.loading;
	const storedPaymentMethods = adyenPaymentMethods.data?.storedPaymentMethods;
	const creditsBalance = getTotalCreditsBalance(shopReadOnly.billing.credits);

	return (
		<>
			<SetPageTitle
				title={t('account.billing.pageTitle', 'Billing')}
				priority={3}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={handleSubmitAll}
						t={t}
					/>
				}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				{creditsBalance.value > 0 && (
					<Banner
						variant="info"
						title={t('shop.billingView.creditsAvailable.title', 'Credits available')}
						description={t('shop.billingView.creditsAvailable.description', {
							amount: getPricingString(creditsBalance.value, creditsBalance.currency),
							defaultValue:
								'You have {{amount}} in credits which will be automatically used when upgrading your plan or paying your invoices',
						})}
					/>
				)}
				<PlanInformation />
				<ContainerBox>
					<Typography variant="h6">
						{t('shop.billingView.paymentCards.title', 'Payment cards')}
					</Typography>
					<Typography variant="body1" color="secondary" gutterBottom>
						{t('shop.billingView.paymentCards.subtitle', 'Manage how you pay your invoices')}
					</Typography>
					{paymentMethodsLoading ? (
						<Spinner relative={true} margin={60} />
					) : !!storedPaymentMethods?.length ? (
						<SavedCards items={storedPaymentMethods} />
					) : (
						<Typography variant="body2" className={classes.noCardsAddedText}>
							{t('shop.billingView.paymentCards.noCards', 'No cards added')}
						</Typography>
					)}
					<AddCardDialog />
				</ContainerBox>
				<ContainerBox>
					<BillingInformation formControl={billingAddressForm.control} />
				</ContainerBox>
				{!!shopPublicInfo && (
					<ContainerBox>
						<Invoices shopPublicInfo={shopPublicInfo} />
					</ContainerBox>
				)}
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		noCardsAddedText: {
			margin: theme.spacing(3, 0),
		},
	}),
);

export default Billing;
